import { useEffect, useMemo, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSearch, faXmark } from '@fortawesome/free-solid-svg-icons'
import { partnerApi } from '../../services/api/partnerApi'
import type {
  PartnerDeviceWithAssignment,
  PartnerDeviceAssignmentFilter,
} from '../../types/partner'

interface RicMultiSelectProps {
  selectedDeviceIds: number[]
  onSelectionChange: (ids: number[]) => void
  /**
   * If true, RICs already assigned to a customer can still be picked
   * (used for re-invites). Defaults to false.
   */
  allowAssigned?: boolean
}

const PAGE_SIZE = 50

export default function RicMultiSelect({
  selectedDeviceIds,
  onSelectionChange,
  allowAssigned = false,
}: RicMultiSelectProps) {
  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState<PartnerDeviceAssignmentFilter>(
    allowAssigned ? 'all' : 'unassigned',
  )
  const [devices, setDevices] = useState<PartnerDeviceWithAssignment[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    partnerApi
      .listDevicesWithAssignment({
        search: search.trim() || undefined,
        assignment: filter,
      })
      .then((res) => {
        if (!cancelled) setDevices(res.devices.slice(0, PAGE_SIZE))
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err?.response?.data?.message || 'Failed to load RICs')
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [search, filter])

  const selectedSet = useMemo(() => new Set(selectedDeviceIds), [selectedDeviceIds])

  const selectedDevices = useMemo(
    () => devices.filter((d) => selectedSet.has(d.deviceId)),
    [devices, selectedSet],
  )

  const toggle = (deviceId: number) => {
    if (selectedSet.has(deviceId)) {
      onSelectionChange(selectedDeviceIds.filter((id) => id !== deviceId))
    } else {
      onSelectionChange([...selectedDeviceIds, deviceId])
    }
  }

  const removeChip = (deviceId: number) => {
    onSelectionChange(selectedDeviceIds.filter((id) => id !== deviceId))
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <FontAwesomeIcon
            icon={faSearch}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
          />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by Sigfox ID or serial"
            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value as PartnerDeviceAssignmentFilter)}
          className="px-3 py-2 border border-gray-300 rounded-lg"
        >
          <option value="unassigned">Unassigned only</option>
          <option value="all">All my RICs</option>
          <option value="assigned">Assigned only</option>
        </select>
      </div>

      {selectedDevices.length > 0 && (
        <div className="flex flex-wrap gap-2 p-2 border border-gray-200 rounded-lg bg-gray-50">
          {selectedDevices.map((d) => (
            <span
              key={d.deviceId}
              className="inline-flex items-center gap-1.5 px-2 py-1 text-sm bg-blue-100 text-blue-800 rounded"
            >
              <span className="font-mono">{d.wisolId}</span>
              <button
                type="button"
                onClick={() => removeChip(d.deviceId)}
                className="hover:text-blue-900"
                aria-label={`Remove ${d.wisolId}`}
              >
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </span>
          ))}
        </div>
      )}

      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <div className="max-h-80 overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 sticky top-0">
              <tr>
                <th className="w-10 px-3 py-2"></th>
                <th className="px-3 py-2 text-left font-medium text-gray-600">Sigfox ID</th>
                <th className="px-3 py-2 text-left font-medium text-gray-600">Serial</th>
                <th className="px-3 py-2 text-left font-medium text-gray-600">HW</th>
                <th className="px-3 py-2 text-left font-medium text-gray-600">Assignment</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={5} className="px-3 py-6 text-center text-gray-500">
                    Loading…
                  </td>
                </tr>
              )}
              {error && !loading && (
                <tr>
                  <td colSpan={5} className="px-3 py-6 text-center text-red-600">
                    {error}
                  </td>
                </tr>
              )}
              {!loading && !error && devices.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-3 py-6 text-center text-gray-500">
                    No RICs match.
                  </td>
                </tr>
              )}
              {!loading &&
                !error &&
                devices.map((d) => {
                  const checked = selectedSet.has(d.deviceId)
                  const disabled = !allowAssigned && d.assignment !== null
                  return (
                    <tr
                      key={d.deviceId}
                      className={`border-t border-gray-100 ${
                        disabled ? 'bg-gray-50 text-gray-400' : 'hover:bg-blue-50 cursor-pointer'
                      }`}
                      onClick={() => !disabled && toggle(d.deviceId)}
                    >
                      <td className="px-3 py-2">
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={disabled}
                          onChange={() => toggle(d.deviceId)}
                          onClick={(e) => e.stopPropagation()}
                          className="rounded border-gray-300"
                        />
                      </td>
                      <td className="px-3 py-2 font-mono">{d.wisolId}</td>
                      <td className="px-3 py-2">{d.serialNumber || '—'}</td>
                      <td className="px-3 py-2">{d.hwVersion}</td>
                      <td className="px-3 py-2">
                        {d.assignment?.kind === 'active' ? (
                          <span className="text-xs text-gray-600">
                            {d.assignment.userEmail} · {d.assignment.groupName}
                          </span>
                        ) : d.assignment?.kind === 'pending' ? (
                          <span className="text-xs text-yellow-700">
                            {d.assignment.email} · pending invite
                          </span>
                        ) : (
                          <span className="text-xs text-green-700">Unassigned</span>
                        )}
                      </td>
                    </tr>
                  )
                })}
            </tbody>
          </table>
        </div>
      </div>

      <p className="text-xs text-gray-500">
        {selectedDeviceIds.length} selected
        {devices.length === PAGE_SIZE && ' · refine search to see more'}
      </p>
    </div>
  )
}
